import { editUserDetails } from '@api/user.js';
import { DrawerAiAnalyser } from '@components/Drawer/AiAnalyzer.js';
import Preview from '@components/Navbar/Preview.js';
import { FULL_SCREEN_PREVIEW_PAGES, NAVBAR_WIDTH } from '@constants/global.js';
import UIIcon from '@icons/UIIcon.js';
import MainDrawer from '@layouts/Drawer.js';
import ProjectsList from '@pages/Projects/index.js';
import { route, subscribe } from '@router/index.js';
import { DEFAULT_RETAINED_ROUTE } from '@router/retainedRoutes.js';
import DrawerState from '@states/DrawerState.js';
import OrganizationState from '@states/Organization.js';
import PreviewState from '@states/Preview.js';
import UserState from '@states/User.js';
import { UIButton, UIDiv } from '@ui';
import { logout } from '@utils/logoutHelper.js';
import OrganisationSwitcher from './OrganisationSwitcher.js';

// Singleton instance to share across components
let previewInstance = null;

// WS: after integration, clean it up to be more specific, no dynamic needed, just exceptions.

export default class Navbar extends UIDiv {
  constructor() {
    super();
    this.setId('navbar');
    this.setClass('navbar-container flex z-[49]');
    this.activeLink = null;
    this.isDesktop = true;
    this.user = UserState.user;
    this.orgs = OrganizationState.organizations;
    this.navbar = this.createSidebar();
    this.highlightClass = 'nav-highlight';
    this.componentCache = {};
    this.isUpdating = false; // Add flag to prevent circular updates
    this.orgSwitcherDesktop = null; // Store reference to org switcher

    // desktop
    const desktopContainer = new UIDiv().setClass(
      `md:!flex flex-col justify-between h-screen p-2 w-[${NAVBAR_WIDTH()}px] !hidden`,
    );

    // top row
    const topContainer = new UIDiv().setClass(
      'flex items-center gap-4 md:flex-col justify-around md:justify-center',
    );
    if (this.user) {
      this.orgSwitcherDesktop = new OrganisationSwitcher({
        orgs: this.orgs,
        placement: 'bottom',
      });
      topContainer.add(this.orgSwitcherDesktop);
    }
    this.addNavLinks(topContainer);

    // bot row
    const bottomContainer = new UIDiv().setClass(
      'flex items-center md:flex-col justify-around md:justify-center',
    );
    this.addDesktopSettings(bottomContainer);

    desktopContainer.add(topContainer, bottomContainer);

    this.navbar.add(desktopContainer);

    UserState.subscribe(user => {
      if (this.isUpdating) return;

      this.user = user;
      topContainer.clear();
      if (this.user) {
        this.orgSwitcherDesktop = new OrganisationSwitcher({
          orgs: this.orgs,
          placement: 'bottom',
        });
        topContainer.add(this.orgSwitcherDesktop);
      }
      this.addNavLinks(topContainer);
      this.addDesktopSettings(bottomContainer);
    }).bind(this);

    OrganizationState.subscribe(orgs => {
      if (this.isUpdating) return;

      this.orgs = orgs;
      // Update the org switcher with new organizations
      if (this.orgSwitcherDesktop) {
        this.orgSwitcherDesktop.setOrganizations(orgs);
      }
      this.addDesktopSettings(bottomContainer);
    }).bind(this);

    this.preview = new Preview();
    // Store the preview instance in the shared variable
    previewInstance = this.preview;

    this.preview.expandButton.onClick(async () => {
      if (this.activeLink) {
        await this.preview.expand(this.activeLink.href);
        if (!FULL_SCREEN_PREVIEW_PAGES.includes(this.activeLink.href)) {
          this.activeLink.link.removeClass(this.highlightClass);
          this.activeLink = null;
        }
      }
    });

    subscribe(() => {
      if (
        window.location.pathname === '/projects' &&
        this.preview.isProjectsPreview &&
        this.preview.isFullScreen
      ) {
        this.collapsePreview();
        const projectsExpandButton = document.querySelector(
          '#expand-projects-toggle',
        );
        projectsExpandButton?.remove();
      }
    });

    this.add(this.navbar);
    this.add(this.preview);
    window.addEventListener('preview:hide', async () => {
      this.collapsePreview();
    });
  }

  getCurrentActivePage = () => {
    return window.location.pathname === '/'
      ? DEFAULT_RETAINED_ROUTE
      : window.location.pathname;
  };

  createSidebar() {
    const sidebar = new UIDiv();
    sidebar.setClass(
      'sidebar text-black border-r border-gray-200 fixed z-10 md:relative md:h-screen bottom-0 left-0 right-0 md:border-r md:flex md:flex-col bg-white',
    );
    return sidebar;
  }

  addNavLinks(container) {
    const navLinks = [
      {
        href: '/spatial-vision',
        icon: 'scan-eye',
        label: 'VLM',
        component: null,
        requiresUser: true,
      },
      {
        href: '/projects',
        icon: 'projects',
        label: 'Projects',
        component: ProjectsList,
      },
    ];
    navLinks.forEach(link => {
      if (link.requiresUser && !this.user) return;
      const extraClass = `p-4 md:p-2 rounded hover:${this.highlightClass} transition-colors flex justify-center nav-btn hover:bg-gray-300`;
      const button = this.createNavButton(
        link.href,
        link.icon,
        link.component,
        extraClass,
        true,
        link.isDrawer,
      );
      button.setAttribute('title', link.label);
      button.setAttribute('aria-label', link.label);
      container.add(button);
    });
  }

  addDesktopSettings(container) {
    container.clear();
    if (!this.user) return;

    const hasSelectedOrg = this.user.selectedOrg;
    if (!hasSelectedOrg && this.orgs?.length > 0) {
      const selectedOrg = this.orgs[0].organizationId;
      this.isUpdating = true;

      editUserDetails({ selectedOrg: selectedOrg })
        .then(() => {
          UserState.setUser({ ...UserState.user, selectedOrg: selectedOrg });
        })
        .catch(error => {
          console.error('Failed to update selected organization:', error);
        })
        .finally(() => {
          this.isUpdating = false;
        });
    }

    const settingsLinks = [
      {
        href: '/logout',
        icon: 'logout',
        label: 'Logout',
        component: null,
        show: true,
      },
    ];

    settingsLinks.forEach(({ href, icon, label, component, show }) => {
      if (!show) return;
      const button = this.createNavButton(
        href,
        icon,
        component,
        `p-4 md:p-2 rounded hover:${this.highlightClass} transition-colors flex justify-center nav-btn hover:bg-gray-300 [&_svg]:fill-none`,
      );
      button.setAttribute('title', label);
      button.setAttribute('aria-label', label);
      container.add(button);
    });
  }

  createNavButton(href, icon, Component, extraClass, expand, isDrawer = false) {
    // Use UILink for navigation items that should be openable in new tabs
    // Use UIButton for drawer items and logout
    const shouldUseLink = !isDrawer && href !== '/logout';
    const button = new UIButton('');
    // shouldUseLink
    // ? new UILink({ href: href })
    // : new UIButton('');

    button.setClass(extraClass);
    if (!isDrawer) button.setAttribute('data-href', href);
    const isActive = href === this.getCurrentActivePage();
    if (isActive) {
      button.addClass(this.highlightClass);
    }
    const iconColor = 'none';
    const strokeColor = isActive ? 'var(--o-hive-primary-100)' : 'black';
    const iconImg = new UIIcon(icon, {
      color: iconColor,
      size: 22,
      strokeColor: strokeColor,
    });
    iconImg.setClass('w-8 h-8');
    button.add(iconImg);

    button.onClick(() =>
      this.handleNavClick(button, href, Component, expand, isDrawer),
    );
    return button;
  }

  handleNavClick(button, href, Component, expand, isDrawer = false) {
    if (isDrawer) {
      const existingDrawer = DrawerState.getContent(href);
      if (!existingDrawer) {
        const aiAnalyzer = new DrawerAiAnalyser();
        MainDrawer.addContent(aiAnalyzer, href);
        MainDrawer.updateTabBar();
      }
      // if we are on viewer page then get the 'drawer-toggle-button' and simulate a click
      if (window.location.pathname.includes('viewer')) {
        const drawerToggleButton = document.querySelector(
          '.drawer-toggle-button',
        );
        if (drawerToggleButton) drawerToggleButton.classList.add('flip');
      }

      MainDrawer.collapseSidebar(href, true);
      return;
    }
    // TODO: keep the highlight on the shrank preview
    const currentActivePage = this.getCurrentActivePage();
    const activeButton = this.navbar.find(
      `[data-href="${currentActivePage}"]`,
    )[0];
    if (activeButton) {
      activeButton.removeClass(this.highlightClass);
      const activeIcon = activeButton.find('svg')[0];
      if (activeIcon) activeIcon.dom.setAttribute('stroke', 'black');
    }
    if (this.preview.isFullScreen) this.preview.shrink();

    const directHrefs = ['/orders', '/settings', '/spatial-vision'];

    document.querySelectorAll('.nav-highlight').forEach(el => {
      el.classList.remove('nav-highlight');
      const icon = el.querySelector('svg');
      if (icon) {
        icon.setAttribute('stroke', 'black');
      }
    });

    if (directHrefs.includes(href)) {
      button.addClass(this.highlightClass);
      const icon = button.find('svg')[0];
      if (icon) {
        icon.setAttribute('stroke', 'var(--o-hive-primary-100)');
      }
      return route(href);
    }
    if (href === '/logout') {
      this.preview.hide();
      logout();
      return;
    }

    if (!Component) return route(href);

    if (this.activeLink?.link === button && this.preview.currentWidth > 0) {
      this.collapsePreview();
      return;
    }

    if (this.activeLink) {
      this.activeLink.link.removeClass(this.highlightClass);
      const icon = this.activeLink.link.find('svg')[0];
      if (icon) icon.dom.setAttribute('stroke', 'black');
    }

    button.addClass(this.highlightClass);
    const icon = button.find('svg')[0];
    if (icon) {
      icon.dom.setAttribute('stroke', 'var(--o-hive-primary-100)');
    }
    this.activeLink = { link: button, href };
    PreviewState.setPreview({
      previewExpanded: false,
    });

    const componentName = Component.name || Component.constructor.name;
    if (!this.componentCache[componentName]) {
      this.componentCache[componentName] = new Component();
    }

    this.preview.show(this.componentCache[componentName], expand);
  }

  collapsePreview() {
    this.preview.hide();
    if (this.activeLink) {
      this.activeLink.link.removeClass(this.highlightClass);
      const icon = this.activeLink.link.find('svg')[0];
      if (icon) icon.dom.setAttribute('stroke', 'black');
    }
    this.activeLink = null;
  }
}

// Export function to get the preview instance
export function getPreviewInstance() {
  return previewInstance;
}
